/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import memoize from 'lodash.memoize';
import throttle from 'lodash.throttle';
import is from 'is_js';

import computBarWidth from '../utils/depthBar';
import {
  getRoundFunc, decRound, formatNumber, formatNumberWithK, ceil, floor,
} from '../utils/number';
import DepthBar from './DepthBar';
import ScrollableTable from './ScrollableTable';
import OrderRowRenderer from './OrderBooks/OrderRowRenderer';
import { setPriceAndAmount as setPriceAndAmountAction } from '../actions/actionsSet/order';
import ShallowDigit from './OrderBooks/ShallowDigit';
import { FlexBox } from './FlexBox';
import feature from '../config/feature';
import PERFORNAMCE from '../config/performance';
import { getLabel } from '../utils/i18n';
import zIndexScale from '../utils/zIndexScale';

const { DEPTH_LIMIT } = PERFORNAMCE;
const DEFAULT_DEPTH_LINE_NUMBER = DEPTH_LIMIT;
const MIN_DEPTH_LINE_NUMBER = 36;
const ORDER_COL_STYLE = { marginLeft: '1px', marginRight: '-3px' };
const PRICE_COL_STYLE = { marginLeft: '5px', overflow: 'visible' };
const AMOUNT_COL_STYLE = { overflow: 'visible' };
const TOTAL_COL_STYLE = { marginRight: '0', overflow: 'visible' };
const PERCENT_COL_STYLE = { position: 'relative' };
const TABLE_ROW_STYLE = { 'cursor': 'pointer' };
const ROW_HEIGHT = 20;

// for performance, avoids creating a RegExp each call
const getPriceGrouperRegexp = memoize(grouping => new RegExp(`^[0-9]*\\.?[0-9]{${grouping}}`));

const { enableVolumnWithK } = feature;

const _formatNumber = enableVolumnWithK ? formatNumberWithK : formatNumber;

const OrderBookBox = styled(FlexBox)`
  width: 100%;
  position: relative;
`;

const Amount = styled.span`
  font-size: 12px;
  width: 100%;
  display: block;
  text-align: right;

  & :hover {
    font-weight: ${({ theme }) => theme.fontWeightBold};
  }
`;
const LeftAmount = styled(Amount)`
  text-align: left;
`;
const BuyPrice = styled(LeftAmount)` color: ${({ theme }) => theme.up}; `;
const SellPrice = styled(LeftAmount)` color: ${({ theme }) => theme.down}; `;

const OrderImg = styled.img.attrs(() => ({
  src: require('./OrderBooks/user-order.png'),
}))`
  width: auto;
`;

const OrderBookHeader = styled(FlexBox)`
  /* fixed width because flex-basis: 280px */
  width: 278px;
  padding: .3em 0;
`;

const OrderBookHeaderCol = styled(FlexBox)`
  .ReactVirtualized__Table__headerColumn {
    margin-left: 0;
    margin-right: 0;
  }
  @supports(-ms-scroll-limit: 0) {
    &:first-child {
      width: 40%;
    }
    &:last-child {
      width: 25%;
    }
    width: 35%;
  }
  &:first-child .ReactVirtualized__Table__headerColumn {
    padding-left: .5em;
  }
  &:first-child {
    padding-left: .5%;
  }
  &:last-child {
    margin-left: auto;
    padding-right: .5%;
  }
`;

const OverLayer = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(240,185,11,0.08);
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: -1;
  ${({ type, active }) => () => {
    if (type === 'bids' && active) {
      return 'border-bottom: 1px dashed #666';
    }
    if (type !== 'bids' && active) {
      return 'border-top: 1px dashed #666';
    }
    return null;
  }}
`;

const TotalBox = styled.div`
  position: absolute;
  right: 0;
  z-index: ${zIndexScale.tooltip};
  margin-right: -7px;
  padding: 8px;
  background: ${({ theme }) => theme.orderBookTotalBoxBg};
  border-radius: 5px;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.15);
  :after {
    content: ' ';
    border: 7px solid transparent;
    border-right-color: ${({ theme }) => theme.orderBookTotalBoxBg};
    position: absolute;
    top: 50%;
    left: 0;
    transform: translate(-100%, -49%);
  }
`;
const Item = styled(FlexBox)`
  width: 100%;
  line-height: 18px;
  font-size: 12px;
  > span {
    margin-right: 30px;
    color: rgba(255,255,255,0.7);
  }
  > strong {
    color: #fff;
    text-align: right;
  }
`;

// const DECIMAL_PLACES = 5;

const mapStateToProps = ({ books, isLoggedIn, openOrders }, { symbol }) => ({
  book: books[symbol], // both bids and asks.
  openOrders,
  isLoggedIn,
});

type Props = {
  i18n: Object,
  book?: Object,
  expand?: string| any,
  type?: string,
  symbol: string,
  both: string[],
  grouping: number | any,
  defaultGrouping: number,
  amountDecimals: number,
  omitTopHeader?: boolean,
  scrollToBottom?: boolean,
  reverse?: boolean,
  openOrders?: Object[],
  scrollable?: boolean,
  setPriceAndAmount?: Function,
  defaultWidth: number,
  defaultHeight: number,
}

type State = {
  scrollTop: number,
  activeIndex: number,
}

type Depth = Array<string | number | boolean>;
type DepthArray = Array<Depth>;

export default
@connect(mapStateToProps, { setPriceAndAmount: setPriceAndAmountAction })
class OrderBook extends React.Component<Props, State> {
  static defaultProps = {
    book: { bids: {}, asks: {} }, // both bids and asks for this symbol.
    both: ['?', '?'], // for invalid market
    type: 'bids',
    reverse: false,
    scrollToBottom: false,
    omitTopHeader: false,
    openOrders: [],
    scrollable: false,
    setPriceAndAmount: () => {},
  }

  _depth: DepthArray;

  _orderBookBox = React.createRef();

  state = {
    activeIndex: -1,
    scrollTop: 0,
  }

  shouldComponentUpdate(): boolean {
    const { expand, type } = this.props;
    if (!!expand && expand !== type) return false;
    return true;
  }

  _generateDepth = (side: Object = {}, type: string, grouping: number) => {
    const { defaultGrouping } = this.props;
    let grouped: { [string]: Array<string | number> } = {};
    if (grouping === defaultGrouping) {
      // grouping is default. do not group.
      grouped = side;
    } else {
      const roundFunc = getRoundFunc(type);
      const groupMatcher = getPriceGrouperRegexp(grouping);
      grouped = Object.keys(side).reduce((_grouped, price) => {
        const parsed = side[price][0];
        const rounded =
          grouping > 0 ?
            decRound(parsed, grouping, roundFunc) :
            Math[roundFunc](parsed).toString();
        const matches = rounded.match(groupMatcher);
        if (!matches || !matches.length) return _grouped;
        const segment = rounded;
        _grouped[segment] = _grouped[segment] ||
            [side[price][0], 0];
        if (typeof _grouped[segment][1] === 'number') {
          _grouped[segment][1] += side[price][2];
        }
        return _grouped;
      }, grouped);
    }
    const depth: DepthArray = Object.keys(grouped).map(price => {
      const [priceInt, total] = grouped[price];
      return [price, priceInt, total]; // int for sorting
    }).sort((a, b) => type === 'bids' ? +b[1] - +a[1] : +a[1] - +b[1]);
    return depth;
  };

  _onAmountClick = (rowData: Depth) => {
    const { type, setPriceAndAmount } = this.props;
    if (!setPriceAndAmount) return;
    const side = type === 'bids' ? 'BUY' : 'SELL';
    let totalAmount = 0;
    let price = 0;
    const lineNumber = this._depth.indexOf(rowData);
    this._depth.forEach((data, index) => {
      if (data[2] !== '–' && lineNumber >= index) {
        totalAmount += Number(data[2]);
        [price] = data;
      }
    });
    if (price !== '–') {
      price = price ? Number(price).toFixed(this.props.defaultGrouping) : 0;
      totalAmount = totalAmount ? totalAmount.toFixed(this.props.amountDecimals) : 0;
      setPriceAndAmount({
        totalAmount, price, side, symbol: this.props.symbol,
      });
    }
  }

  // getters should be very simple, renderers can be more complex.

  _rowGetter = ({ index }: { index: number }) => {
    const { reverse } = this.props;
    const depth = this._depth;
    return reverse && depth.length > 0 ? depth[depth.length - index - 1] : depth[index];
  }

  _headerRowRenderer = ({ columns }: { columns: typeof React.Children }) => (
    this.props.omitTopHeader ? null : (
      <OrderBookHeader wrap="nowrap" role="row">
        <OrderBookHeaderCol basis="35%">{columns[1]}</OrderBookHeaderCol>
        <OrderBookHeaderCol>{columns[2]}</OrderBookHeaderCol>
        <OrderBookHeaderCol>{columns[3]}</OrderBookHeaderCol>
      </OrderBookHeader>
    ));

  _rowRenderer = (props: Object) => <OrderRowRenderer {...props} />;

  _cellGetterOrder = ({ rowData }: { rowData: Depth }) => rowData

  _cellRendererOrder= ({ rowData }: { rowData: Depth }) => rowData[4] ? <OrderImg /> : null

  _cellGetterPrice = ({ rowData }: { rowData: Depth }) => rowData[0]

  _cellRendererPrice = ({ cellData, rowIndex }: { cellData: string, rowIndex: number }) => this.props.type === 'bids' ? (
    <BuyPrice onClick={() => this._onPriceClick(cellData)}>{cellData === '–' ?
      cellData : (
        <ShallowDigit
          value={decRound(cellData, this.props.grouping, 'floor')}
          preValue={this._depth[rowIndex - 1] && this._depth[rowIndex - 1][0]}
          side={this.props.type}
          digit={this.props.grouping}
        />
      )}
    </BuyPrice>
  ) : (
    <SellPrice onClick={() => this._onPriceClick(cellData)}>{cellData === '–' ?
      cellData : (
        <ShallowDigit
          value={decRound(cellData, this.props.grouping, 'floor')}
          preValue={
                  this._depth[this._depth.length - rowIndex - 2] &&
                  this._depth[this._depth.length - rowIndex - 2][0]
                }
          digit={this.props.grouping}
        />
      )}
    </SellPrice>
  );

  _cellRendererAmount = ({ rowData }: { rowData: Depth }) => (
    <Amount onClick={() => this._onAmountClick(rowData)}>
      {rowData[2] === '–' ?
        rowData[2] :
        decRound(
          (+rowData[2] * +rowData[1]) / +rowData[1],
          this.props.amountDecimals,
          undefined, true)}
    </Amount>
  )

  _cellRendererTotal = ({ rowData }: { rowData: Depth }) => (
    <Amount onClick={() => this._onAmountClick(rowData)}>{rowData[2] === '–' ?
      rowData[2] :
      _formatNumber(+rowData[2] * +rowData[1],
        Math.min(this.props.amountDecimals + this.props.defaultGrouping, 8))}
    </Amount>
  )

  _cellRendererPercent = ({ rowData }: { rowData: Depth }) => this.props.type === 'bids' ?
    <DepthBar width={+rowData[3]} backgroundColor="rgba(112, 168, 0, 0.1)" /> :
    <DepthBar width={+rowData[3]} backgroundColor="rgba(202, 44, 120, 0.1)" />

    _cellRendererOverLayer = ({ rowIndex }: { rowIndex: number }) => {
      const { type } = this.props;
      const { activeIndex } = this.state;
      if (activeIndex > -1 && ((rowIndex <= activeIndex && type === 'bids') || (rowIndex >= activeIndex && type !== 'bids'))) {
        return <OverLayer type={type} active={activeIndex === rowIndex} />;
      }
      return null;
    }

    _getScrollTop = (scrollTop: number) => {
      this.setState({
        scrollTop,
      });
    }

    _handleRowMouseOver = (props: Object) => {
      const { index } = props;
      this.setState({ activeIndex: index });
    }

    _getPosition = () => {
      const { activeIndex, scrollTop } = this.state;
      const { type } = this.props;
      const totalLen = this._depth.length;
      if (type === 'bids') {
        return { top: `${(Number(activeIndex) + 1) * ROW_HEIGHT - scrollTop}px`, transform: 'translate(100%, -50%)' };
      }
      // $FlowFixMe
      const { current: { clientHeight: orderBookBoxHeight } } = this._orderBookBox;
      const h = (totalLen + 1) * ROW_HEIGHT - scrollTop - orderBookBoxHeight;
      return { bottom: `${(totalLen - Number(activeIndex)) * ROW_HEIGHT - h}px`, transform: 'translate(100%, 50%)' };
    }

    _getTotal = () => {
      const { activeIndex } = this.state;
      if (activeIndex === -1) return { totalAmount: 0, totalVolumn: 0 };
      const { type } = this.props;
      const depth = [...this._depth];
      const data = type === 'bids' ? depth.slice(0, activeIndex + 1) : depth.reverse().slice(activeIndex, depth.length);
      let totalAmount = 0;
      let totalVolumn = 0;
      data.map((item) => {
        if (item[1] !== '–') {
          totalAmount += Number(item[2]);
          totalVolumn += Number(item[1]) * Number(item[2]);
        }
        return null;
      });
      return { totalAmount, totalVolumn };
    }


    _setUserOrders(
      asksOrBids: DepthArray, type?: string, orders?: Array<Object>, depthMergeUnit: number
    ) {
      const { symbol } = this.props;
      asksOrBids && asksOrBids.forEach((val) => {
        if (val[0] === '–') return;
        val[4] = false;
        let value;
        orders && orders.forEach(d => {
          if (d.symbol === symbol) {
            let price = Number(d.price);
            if (type === 'asks') {
              price = ceil(price, depthMergeUnit);
              value = ceil(+val[1], depthMergeUnit);
            } else if (type === 'bids') {
              price = floor(price, depthMergeUnit);
              value = floor(+val[1], depthMergeUnit);
            }
            if (value === price) {
              val[4] = true;
            }
          }
        });
      });
    }

    _addBlankDepth(oriDepth: DepthArray, isDefaultGrouping:boolean) {
      const lineNumber = isDefaultGrouping ? DEFAULT_DEPTH_LINE_NUMBER : MIN_DEPTH_LINE_NUMBER;
      while (oriDepth.length < lineNumber) {
        oriDepth.push(['–', '–', '–', 0]);
      }
    }


    _onPriceClick(price: string) {
      const { type, symbol, setPriceAndAmount } = this.props;
      if (!setPriceAndAmount) return;
      const side = type === 'bids' ? 'BUY' : 'SELL';
      setPriceAndAmount({
        price, side, symbol,
      });
    }

    render() {
      const {
        i18n,
        both,
        book,
        type,
        grouping,
        defaultGrouping,
        amountDecimals,
        scrollToBottom,
        openOrders,
        scrollable,
        defaultWidth,
        defaultHeight,
        omitTopHeader,
      } = this.props;

      const { activeIndex } = this.state;

      const bids = this._generateDepth(book && book.bids, 'bids', grouping);
      const asks = this._generateDepth(book && book.asks, 'asks', grouping);
      const sideData = computBarWidth(bids, asks)[type];
      const sideSize = book && Object.keys(book[type] || {}).length;
      const isDefaultGrouping = grouping === defaultGrouping;

      this._addBlankDepth(sideData, isDefaultGrouping);
      this._setUserOrders(sideData, type, openOrders, grouping);
      const depth = this._depth = sideData;

      let scrollToIndex = 0;
      if (scrollToBottom &&
        book &&
        // is.empty(depth) does not make sense, it will always return false
        !is.empty(book.bids) &&
        !is.empty(book.asks)) {
        scrollToIndex = Math.min(
          depth.length - 1,
          Number.MAX_VALUE,
        );
      }
      const { totalAmount, totalVolumn } = this._getTotal();
      return (
        <OrderBookBox ref={this._orderBookBox} onMouseLeave={() => this.setState({ activeIndex: -1 })}>
          { activeIndex !== -1 && (
          <TotalBox style={{ ...this._getPosition() }}>
            <Item justify="space-between">
              <span>{getLabel('exchange-orderBook-avgPrice', i18n)}: </span>
              <strong>≈ {formatNumber(totalVolumn / totalAmount, grouping)}</strong>
            </Item>
            <Item justify="space-between">
              <span>{getLabel('exchange-orderBook-Sum', i18n)} {both[0]}: </span>
              <strong>{ formatNumber(totalAmount, amountDecimals) }</strong>
            </Item>
            <Item justify="space-between">
              <span>{getLabel('exchange-orderBook-Sum', i18n)} {both[1]}: </span>
              <strong>{ formatNumber(totalVolumn, Math.min(amountDecimals + defaultGrouping, 8)) }</strong>
            </Item>
          </TotalBox>
          )}
          <ScrollableTable
            defaultWidth={defaultWidth}
            defaultHeight={defaultHeight}
            disableHeader={omitTopHeader}
            headerRowRenderer={this._headerRowRenderer}
            headerHeight={omitTopHeader ? 0 : 20}
            rowHeight={ROW_HEIGHT}
            overscanRowCount={15}
            rowCount={depth.length}
            realRowCount={sideSize}
            rowGetter={this._rowGetter}
            rowRenderer={this._rowRenderer}
            nonce={typeof window !== 'undefined' && window.__NONCE__}
            scrollable={scrollable}
            rowStyle={TABLE_ROW_STYLE}
            nonScrollableScrollIndex={scrollToIndex}
            book={book}
            onRowMouseOver={throttle(this._handleRowMouseOver, 300)}
            getScrollTop={this._getScrollTop}
          >
            <ScrollableTable.Column
              dataKey="order"
              width={20}
              cellDataGetter={this._cellGetterOrder}
              cellRenderer={this._cellRendererOrder}
              style={ORDER_COL_STYLE}
            />
            <ScrollableTable.Column
              label={`${getLabel('exchange-trade-Price', i18n)}(${both && both.length > 1 ? both[1] : ''})`}
              dataKey="price"
              width={310}
              flexGrow={1}
              flexShrink={1}
              cellDataGetter={this._cellGetterPrice}
              cellRenderer={this._cellRendererPrice}
              style={PRICE_COL_STYLE}
            />
            <ScrollableTable.Column
              label={`${getLabel('exchange-common-amount', i18n)} (${both && both.length ? both[0] : ''})`}
              dataKey="amt"
              width={320}
              flexGrow={1}
              flexShrink={1}
              cellRenderer={this._cellRendererAmount}
              style={AMOUNT_COL_STYLE}
            />
            <ScrollableTable.Column
              label={`${getLabel('exchange-trade-Total', i18n)}(${both && both.length > 1 ? both[1] : ''})`}
              dataKey="total"
              width={385}
              flexGrow={1}
              flexShrink={1}
              cellRenderer={this._cellRendererTotal}
              style={TOTAL_COL_STYLE}
            />
            <ScrollableTable.Column
              dataKey="percent"
              width={0}
              cellRenderer={this._cellRendererPercent}
              style={{ relative: PERCENT_COL_STYLE }}
            />
            <ScrollableTable.Column
              dataKey="overLayer"
              width={0}
              cellRenderer={this._cellRendererOverLayer}
              style={{ relative: PERCENT_COL_STYLE }}
            />
          </ScrollableTable>
        </OrderBookBox>
      );
    }
}
